import {Component, OnDestroy, OnInit} from '@angular/core';
import { CommonModule } from '@angular/common';
import {BsModalRef} from 'ngx-bootstrap/modal';
import {IMFAItem} from '../interface';
import {MfaService} from '../mfa.service';
import {DialogHeaderComponent} from '@reusable/dialog-header/dialog-header.component';
import {TranslateModule} from '@ngx-translate/core';
import {ButtonComponent} from '../../../../controls/button/button.component';
import {FormsModule} from '@angular/forms';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {NgSelectModule} from '@ng-select/ng-select';
import {MultiSelectComponent} from '../../../../controls/multi-select/multi-select.component';
import {ClientManagementService} from '@services/client-management.service';
import {NotificationService} from '@notifications';
import {Subscription, timer} from 'rxjs';
import {first, switchMap} from 'rxjs/operators';
import {IFormField} from '../../fde-long-polling/interface';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {ConfirmationPopupService} from '@services/confirmation-popup.service';

@Component({
  selector: 'app-mfa-modal',
  standalone: true,
  imports: [CommonModule, DialogHeaderComponent, TranslateModule, ButtonComponent, FormsModule, MatCheckboxModule, NgSelectModule, MultiSelectComponent, MatProgressSpinner],
  templateUrl: './mfa-modal.component.html',
  styleUrls: ['./mfa-modal.component.scss']
})
export class MfaModalComponent implements OnInit, OnDestroy {
  data: IMFAItem;
  clientPhone = '';
  isSendingSms = false;
  isWaitingForClientResponse = false;
  isPasswordVisible = false;
  private smsPollingSubscription?: Subscription;
  private readonly smsAllowedFieldTypes = ['TEXT', 'PASSWORD', 'TOKEN'];

  constructor(
    private modalRef: BsModalRef,
    private mfaService: MfaService,
    private notificationService: NotificationService,
    private clientManagementService: ClientManagementService,
    private confirmationPopupService: ConfirmationPopupService,
  ) {
  }

  ngOnInit() {
    this.initClientPhone(this.getClientUsername());
  }

  ngOnDestroy() {
    this.stopSmsPolling();
  }

  close(result: boolean) {
    this.stopSmsPolling();
    this.mfaService.done(result, {
      ...this.data,
    });
    this.modalRef.hide();
  }

  submit() {
    this.close(true);
  }

  canSubmit(): boolean {
    const formFields = this.data?.form?.metadata?.form?.formFields || [];
    return formFields.every((field) => {
      if (!field.mandatory) {
        return true;
      }
      if (field.fieldType === 'MULTISELECT') {
        return !!field.msAnswer?.length;
      }
      return field.answer !== undefined && field.answer !== null && field.answer !== '';
    });
  }

  shouldEnableSms() {
    return !!this.getSmsEligibleField() && !!this.clientPhone;
  }

  onSendSMS() {
    const formField = this.getSmsEligibleField();
    const clientUsername = this.getClientUsername();
    const bankAccountId = this.data?.connectionId;

    const phone = this.clientPhone;
    if (!formField || !clientUsername || !bankAccountId || !phone) {
      return;
    }

    const clientName = this.data?.clientName || 'Client';
    this.confirmationPopupService.confirm({
      title: 'Confirm SMS Request',
      text: `${clientName} will receive an SMS containing the MFA security question. Do you want to proceed?`,
      yes: () => {
        this.isSendingSms = true;
        this.mfaService.sendFdeMfaSms({
          clientUsername,
          bankAccountId,
          securityQuestion: formField.label,
          phoneTo: phone
        })
          .pipe(first())
          .subscribe({
            next: () => {
              this.isSendingSms = false;
              this.isWaitingForClientResponse = true;
              this.notificationService.info('Waiting for client response');
              this.startSmsPolling(clientUsername, bankAccountId);
            },
            error: (error) => {
              this.isSendingSms = false;
              this.notificationService.error(error?.error?.errorMsg || 'Failed to send SMS to client');
            }
          });
      },
    });
  }

  private initClientPhone(username: string) {
    if (!username) {
      this.clientPhone = '';
      return;
    }

    this.clientManagementService.get()
      .pipe(first())
      .subscribe({
        next: (clients) => {
          const client = (clients || []).find((item) => item?.username === username);
          this.clientPhone = client?.phone || '';
        },
        error: () => this.clientPhone = '',
      });
  }

  private startSmsPolling(clientUsername: string, bankAccountId: number) {
    this.stopSmsPolling();
    this.smsPollingSubscription = timer(30000, 5000)
      .pipe(switchMap(() => this.mfaService.getFdeMfaSmsResponse(clientUsername, bankAccountId)))
      .subscribe({
        next: (response) => {
          if (!response?.answered) {
            return;
          }

          this.isWaitingForClientResponse = false;
          this.stopSmsPolling();

          const formField = this.getSmsEligibleField();
          if (!formField) {
            return;
          }
          formField.answer = response.answer || '';
          this.submit();
        },
        error: (error) => {
          this.isWaitingForClientResponse = false;
          this.stopSmsPolling();
          this.notificationService.error(error?.error?.errorMsg || 'Failed to get client response');
        }
      });
  }

  private getSmsEligibleField(): IFormField | null {
    const formFields = this.data?.form?.metadata?.form?.formFields || [];
    if (formFields.length !== 1) {
      return null;
    }
    const formField = formFields[0];
    if (!this.smsAllowedFieldTypes.includes(formField.fieldType)) {
      return null;
    }
    return formField;
  }

  private getClientUsername() {
    return this.data?.clientUsername || '';
  }

  private stopSmsPolling() {
    if (this.smsPollingSubscription) {
      this.smsPollingSubscription.unsubscribe();
      this.smsPollingSubscription = undefined;
    }
  }
}
